import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { debounce } from 'lodash-es';
import { useStateInRef } from 'seagull';
import { routes } from '~/constants';
import { useIoContext, useStore, useStoreActions } from '~/contexts';
import { LAST_USER_INTERACTION_LOCAL_STORAGE_KEY } from '~/infra/session-storage';
import { selectors } from '~/store/state-selectors';
import { useAppConfig } from '~/store/store-context';
const defaultUserInputDebounceTime = 500;
const uiEvents = [
    'click',
    'keydown'
];
export function useSessionTimeout(disableOnMount) {
    const getDisableOnMount = useStateInRef(disableOnMount);
    const timeoutRef = useRef();
    const didMount = useRef(false);
    const navigate = useNavigate();
    const { localStorage, getNow, cookies, browser: { reloadAt, registerBecameVisibleListener } } = useIoContext();
    const { reset } = useStoreActions();
    const isLoggedIn = useStore((state)=>!!state.userInfo && state.authFlowDone);
    const sessionTimeoutMS = useStore(selectors.sessionTimeoutMS);
    const { inputDebounceTime } = useAppConfig();
    useEffect(()=>{
        const sessionTimeoutState = {
            sessionTimeout: true
        };
        let sessionEnded = false;
        function claimSessionEnd() {
            if (sessionEnded) {
                return false;
            }
            sessionEnded = true;
            stopSessionTimeout();
            return true;
        }
        function endSessionInPlace() {
            if (claimSessionEnd()) {
                reset();
                navigate(routes.home, {
                    state: sessionTimeoutState
                });
            }
        }
        function endSessionByReload() {
            if (claimSessionEnd()) {
                cookies.clearAuthToken();
                cookies.trySignalAuthTokenChange();
                reloadAt(routes.home, sessionTimeoutState);
            }
        }
        function armSessionTimeout(delayMS) {
            timeoutRef.current && clearTimeout(timeoutRef.current);
            timeoutRef.current = setTimeout(()=>{
                if (!isLoggedIn) return;
                endSessionByReload();
            }, delayMS);
        }
        function initSessionTimeout(updateLocalStorage) {
            if (!isLoggedIn) {
                return;
            }
            if (updateLocalStorage) {
                localStorage.setItem(LAST_USER_INTERACTION_LOCAL_STORAGE_KEY, getNow().getTime().toString());
            }
            armSessionTimeout(sessionTimeoutMS);
        }
        function readLastUserInteraction() {
            const storedValue = localStorage.getItem(LAST_USER_INTERACTION_LOCAL_STORAGE_KEY);
            if (storedValue == null) {
                return null;
            }
            const timestampMS = Number(storedValue);
            return Number.isFinite(timestampMS) ? timestampMS : null;
        }
        function reconcileSessionTimeout(endExpiredSession) {
            if (!isLoggedIn) {
                return;
            }
            const lastUserInteraction = readLastUserInteraction();
            if (lastUserInteraction == null) {
                endExpiredSession();
                return;
            }
            const idleMS = getNow().getTime() - lastUserInteraction;
            if (idleMS >= sessionTimeoutMS || idleMS <= -sessionTimeoutMS) {
                endExpiredSession();
                return;
            }
            armSessionTimeout(sessionTimeoutMS - Math.max(0, idleMS));
        }
        function stopSessionTimeout() {
            timeoutRef.current && clearTimeout(timeoutRef.current);
        }
        function forgetInteractionOfEndedSession() {
            if (cookies.getAuthToken() == null) {
                localStorage.removeItem(LAST_USER_INTERACTION_LOCAL_STORAGE_KEY);
            }
        }
        const debouncedInitSessionTimeout = debounce(initSessionTimeout, inputDebounceTime != null ? inputDebounceTime : defaultUserInputDebounceTime);
        function onInteractionRecordedInAnotherTab(event) {
            if (event.key === LAST_USER_INTERACTION_LOCAL_STORAGE_KEY && event.newValue != null) {
                initSessionTimeout(false);
            }
        }
        function onUserInteraction() {
            debouncedInitSessionTimeout(true);
        }
        window.addEventListener('storage', onInteractionRecordedInAnotherTab);
        uiEvents.forEach((event)=>window.addEventListener(event, onUserInteraction));
        const unregisterBecameVisibleListener = registerBecameVisibleListener(()=>reconcileSessionTimeout(endSessionByReload));
        if (!didMount.current) {
            forgetInteractionOfEndedSession();
        }
        if (isLoggedIn && (didMount.current || !getDisableOnMount())) {
            const isRestoredSession = readLastUserInteraction() != null;
            if (isRestoredSession) {
                reconcileSessionTimeout(endSessionInPlace);
            } else {
                initSessionTimeout(true);
            }
        }
        didMount.current = true;
        return ()=>{
            window.removeEventListener('storage', onInteractionRecordedInAnotherTab);
            uiEvents.forEach((event)=>window.removeEventListener(event, onUserInteraction));
            unregisterBecameVisibleListener();
            stopSessionTimeout();
            debouncedInitSessionTimeout.cancel();
        };
    }, [
        isLoggedIn,
        sessionTimeoutMS
    ]);
}
